/**
 * useCancelReasonState — le motif choisi et sa précision, et la sortie vers l'étape 2.
 *
 * Aucun appel de service : l'étape 1 est la seule du parcours qui ne coûte rien
 * (`CancelReasonScreen.js` n'a ni `connect` ni `apiRequest`). Un `useReducer` parce que les deux
 * champs bougent ensemble : choisir un autre motif ne doit pas laisser traîner la précision de
 * l'ancien.
 */
import { useCallback, useEffect, useMemo, useReducer } from "react"
import { useNavigation } from "@react-navigation/native"

import {
    cancelReasonRequiresDetail,
    CANCEL_REASONS,
} from "../../../../adapters/parent/SubscriptionCancel/subscriptionCancelAdapter"
import { useGesture } from "../../../hooks/gestureTracking"
import { nextScreenAfterReason, type CancelFlowNavigation, type CancelFlowRouteParams } from "./cancelFlowNavigation"

type State = {
    selectedReason: string | null
    detail: string
}

type Action = { type: "SELECT_REASON"; key: string } | { type: "SET_DETAIL"; detail: string }

const initialState: State = { selectedReason: null, detail: "" }

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "SELECT_REASON":
            if (state.selectedReason === action.key) return state
            // Changer de motif efface la précision : elle appartenait au motif précédent.
            return { selectedReason: action.key, detail: "" }
        case "SET_DETAIL":
            return { ...state, detail: action.detail }
        default:
            return state
    }
}

export type UseCancelReasonStateArgs = {
    routeParams: CancelFlowRouteParams
}

export function useCancelReasonState(args: UseCancelReasonStateArgs) {
    const { routeParams } = args
    const navigation = useNavigation<CancelFlowNavigation>()
    const [state, dispatchUi] = useReducer(reducer, initialState)

    // La première étape de la résiliation : le parcours s'ouvre (l'envoi, lui, part de la dernière).
    const gesture = useGesture("subscription.cancel")
    useEffect(() => gesture.opened(), [gesture])

    const onContinue = useCallback(() => {
        const motif = state.selectedReason
        if (motif == null) return
        const detail = state.detail.trim()
        // `motif_free_text` n'est posé que par le motif qui l'exige — le backend le valide en
        // `required_if:motif,other` (`CancelReasonScreen.js:31-35`).
        if (cancelReasonRequiresDetail(motif) && detail.length === 0) return

        navigation.push(nextScreenAfterReason(), {
            ...routeParams,
            motif,
            motif_free_text: cancelReasonRequiresDetail(motif) ? detail : null,
        })
    }, [navigation, routeParams, state.selectedReason, state.detail])

    const actions = useMemo(
        () => ({
            onSelectReason: (key: string) => dispatchUi({ type: "SELECT_REASON", key }),
            onDetailChange: (detail: string) => dispatchUi({ type: "SET_DETAIL", detail }),
            onContinue,
            onBack: () => navigation.goBack(),
            onClose: () => navigation.popToTop(),
        }),
        [navigation, onContinue]
    )

    return {
        reasons: CANCEL_REASONS,
        selectedReason: state.selectedReason,
        detail: state.detail,
        actions,
    }
}
